// src/modules/assetManagement/management/UploadQueueContext.tsx
// One upload queue and upload form draft for all Customer Manager pages, so files, tags and links
// survive a trip to Create Event / Create Product and uploads keep running elsewhere in the app.
import React, { createContext, useContext, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import type { ReturnTo } from "../createReturn";
import type { LinkSelection } from "../linkRules";
import { useUploadQueue } from "./useUploadQueue";

type Queue = ReturnType<typeof useUploadQueue>;

export interface UploadDraft {
  tags: string[];
  links: LinkSelection;
  /** Product sent to Create Event; selected too when that event comes back containing it */
  pendingProductId?: string;
  /** Files of the upload in progress; when all succeed the user is sent on */
  batchKeys?: string[];
  /** Page that opened Upload; the user goes back there after a successful upload */
  returnTo?: ReturnTo;
}

interface Shared {
  queue: Queue;
  draft: UploadDraft;
  setDraft: React.Dispatch<React.SetStateAction<UploadDraft>>;
}

const UploadQueueContext = createContext<Shared | null>(null);

const EMPTY_DRAFT: UploadDraft = { tags: [], links: { eventIds: [], productIds: [] } };

/** Wrap the Customer Manager layout with this (App.tsx) */
export const UploadQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queue = useUploadQueue();
  const [draft, setDraft] = useState<UploadDraft>(EMPTY_DRAFT);
  const value = useMemo(() => ({ queue, draft, setDraft }), [queue, draft]);
  return <UploadQueueContext.Provider value={value}>{children}</UploadQueueContext.Provider>;
};

/** Layout route element for /asset-management/*; reuses the provider above it when there is one */
export const UploadQueueLayout: React.FC = () => {
  const outer = useContext(UploadQueueContext);
  if (outer) return <Outlet />;
  return (
    <UploadQueueProvider>
      <Outlet />
    </UploadQueueProvider>
  );
};

const useShared = (): Shared => {
  const shared = useContext(UploadQueueContext);
  if (!shared) throw new Error("Upload queue used outside UploadQueueProvider");
  return shared;
};

export const useSharedUploadQueue = (): Queue => useShared().queue;

export const useUploadDraft = () => {
  const { draft, setDraft } = useShared();
  return { draft, setDraft };
};